/**
 * FAA UAS Facility Map — ArcGIS FeatureServer client
 *
 * Fetches airspace grid polygons from the FAA Facility Map service using
 * spatial (bounding-box) queries so only features visible in the current
 * camera viewport are returned.
 *
 * Data source:
 *   https://services6.arcgis.com/ssFJjBXIUyZDrSYZ/arcgis/rest/services/
 *   FAA_UAS_FacilityMap_Data/FeatureServer/0
 */

import { Color } from 'cesium';

// ── ArcGIS endpoint ──────────────────────────────────────────────────────────

const FEATURE_SERVER_URL =
  'https://services6.arcgis.com/ssFJjBXIUyZDrSYZ/arcgis/rest/services/FAA_UAS_FacilityMap_Data/FeatureServer/0/query';

// ── Ceiling → Color mapping (matches official FAA app) ──────────────────────

export const CEILING_COLORS: Record<number, Color> = {
  0: Color.fromCssColorString('#B53535'),   // dark red
  50: Color.fromCssColorString('#BCFCB6'),  // light green
  100: Color.fromCssColorString('#E69800'), // orange
  150: Color.fromCssColorString('#FFBEBE'), // pale pink
  200: Color.fromCssColorString('#D3FFBE'), // pale green
  250: Color.fromCssColorString('#005CE6'), // blue
  300: Color.fromCssColorString('#73B2FF'), // light blue
  350: Color.fromCssColorString('#00A884'), // teal
  400: Color.fromCssColorString('#38A800'), // dark green
};

/** Fallback color when CEILING value is unexpected */
export const DEFAULT_FACILITY_COLOR = Color.GRAY;

// ── GeoJSON types (minimal, for the data we receive) ────────────────────────

export interface FAAFeature {
  type: 'Feature';
  properties: {
    CEILING: number;
    [key: string]: unknown;
  };
  geometry: Record<string, unknown>;
}

export interface FAAFeatureCollection {
  type: 'FeatureCollection';
  features: FAAFeature[];
}

// ── Spatial bounding-box fetch ──────────────────────────────────────────────

/**
 * Fetch FAA UAS Facility Map features within a bounding box.
 * Uses ArcGIS FeatureServer spatial query — typically returns hundreds
 * of features for a zoomed-in viewport, not the full 378K dataset.
 *
 * @param west   - Western longitude in degrees
 * @param south  - Southern latitude in degrees
 * @param east   - Eastern longitude in degrees
 * @param north  - Northern latitude in degrees
 * @param signal - Optional AbortSignal to cancel in-flight requests
 */
export async function fetchFAAFacilitiesInBBox(
  west: number,
  south: number,
  east: number,
  north: number,
  signal?: AbortSignal
): Promise<FAAFeatureCollection> {
  const params = new URLSearchParams({
    where: '1=1',
    geometry: `${west},${south},${east},${north}`,
    geometryType: 'esriGeometryEnvelope',
    spatialRel: 'esriSpatialRelIntersects',
    outFields: 'CEILING',
    f: 'geojson',
  });

  const response = await fetch(`${FEATURE_SERVER_URL}?${params}`, { signal });

  if (!response.ok) {
    throw new Error(
      `FAA Facility Map fetch failed: ${response.status} ${response.statusText}`
    );
  }

  const data = (await response.json()) as FAAFeatureCollection;

  return {
    type: 'FeatureCollection',
    features: data.features || [],
  };
}
